<template>
  <div
    ref="filterItem"
    v-click-outside="hideDropdown"
    class="d-block d-md-inline-block position-relative filter-item"
    :data-test-id="'filter-item__' + title.trim().replace(' ', '')"
    :class="{ 'filter-item__active': show }"
  >
    <BButton
      class="dropdown-toggle text-start w-100 justify-content-start"
      variant="outline-light"
      :disabled="disabled"
      :class="{ 'active': show }"
      :style="'min-width: ' + filterWidth + 'px'"
      @click="toggleDropdown"
    >
      <slot name="icon" />
      {{ title }}
    </BButton>
    <div
      ref="filterOptions"
      class="filter-item__options list-unstyled"
      data-test-id="filter-item__options"
      :class="{ 'w-100': shouldStretch, 'invisible': !show }"
    >
      <slot v-if="show" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {nextTick, onMounted, ref, watch} from 'vue'
import {useCommon} from '@/composables/useCommon'

const props = withDefaults(defineProps<{
  title: string
  options?: any[]
  disabled?: boolean
  width?: string
}>(), {
  options: () => [],
  width: 'auto'
})

const emit = defineEmits<{(e: 'show'): void
  (e: 'hide'): void
  (e: 'updated'): void
}>()

const {labels} = useCommon()

const filterItem = ref<HTMLElement | null>(null)
const filterOptions = ref<HTMLElement | null>(null)
const show = ref(true)
const filterWidth = ref<string | number>(props.width)
const shouldStretch = ref(false)
const internalOptions = ref<any[]>([])

watch(show, (_v) => {
  if (_v && props.options) {
    internalOptions.value = props.options.map((x) => x?.value)
    emit('show')
  } else if (internalOptions.value.length && props.options.length) {
    if (props.options.filter((x, i) => internalOptions.value[i] !== x?.value).length) emit('updated')
    emit('hide')
  } else {
    emit('hide')
  }
})

watch(labels, () => {
  recalculateWidth()
}, {deep: true})

onMounted(() => {
  recalculateWidth()
  show.value = false
})

function hideDropdown(): void {
  show.value = false
}

function showDropdown(): void {
  show.value = true
}

function toggleDropdown(): void {
  if (!props.disabled) show.value = !show.value
}

function recalculateWidth(): void {
  if (filterWidth.value === 'unset') {
    nextTick(() => {
      filterWidth.value
    })
  } else {
    nextTick(() => {
      filterWidth.value = (filterOptions.value?.clientWidth || 0) + 2
      shouldStretch.value = Number(filterWidth.value) < (filterItem.value?.clientWidth || 0)
    })
  }
}

defineExpose({
  hideDropdown,
  showDropdown
})
</script>

<style lang="scss">
@import '../../../scss/grid';
@import '../../../scss/colors';
@import '../../../scss/z-index';

.filter-item {
  margin: .25rem 0;

  @include media-breakpoint-up('md') {
    margin: .5rem .5rem 0 0;
  }

  &__active {
    z-index: $zindex-dropdown;
  }

  &__options {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding-top: 2.5rem;
    box-shadow: 0 .15rem .5rem rgba($black, .35);
    z-index: $zindex-dropdown;

    &>div,
    &>li {
      background-color: $white !important;
    }

    @include media-breakpoint-down('sm') {
      right: 0;
    }
  }

  &.wide {
    width: 100%;

    .filter-item__options {
      right: 0;
    }

    button {
      width: 100%;
    }
  }

  &.smallest {
    width: 40%;

    .filter-item__options {
      right: 0;
    }

    button {
      width: 40%;
    }

    @include media-breakpoint-down('sm') {
      width: 87%;
      margin-left: 1.5rem !important;
    }
  }

  button.dropdown-toggle {
    padding-right: 1.675rem;
    background: $gray-100;
    border-color: $gray-400;
    color: $gray-800;

    @include media-breakpoint-down('sm') {
      width: 100%;
    }

    &:after {
      position: absolute;
      top: calc(50% - .255rem);
      right: .75rem;
      transition: .2s all;
    }

    &.active {
      z-index: 1;

      &:active,
      &:focus,
      & {
        box-shadow: none !important;
        outline: none;
        border-color: transparent;
        border-bottom-color: $gray-200;
        background: transparent !important;
      }

      &:after {
        transform: rotate(180deg);
      }
    }
  }

  &.is-invalid {
    button {
      border-color: $danger;
    }
  }

  &.dropdown {

    &.is-placeholder {
      button.dropdown-toggle {
        color: $gray-700;
      }
    }

    button.dropdown-toggle {
      color: $gray-900;
      background: $white;
      font-family: "Helvetica Neue", Helvetica, Roboto, sans-serif;
    }
  }
}
</style>
